import { Control } from '@yleisradio/areena-types';
import React from 'react';
import { isActivator } from '../typeGuards';
import styles from './SidebarItem.module.scss';
import { getTranslation } from '../localizations/translations';
import { getAssetIconWithFallback } from './getIcons';
import { useProfilesLocalStorage } from '../ProfilesLocalStorageContext';
import ReactRouterLink from '../Routes/ReactRouterLink';
import { cls } from '../View/utils';
import { ProfileIconButton } from '../common/ProfileIconButton/ProfileIconButton';
import { useAppFeaturesContext } from '../contexts/AppFeaturesContext/AppFeaturesContext';
import { useAuthContext } from '../contexts/AuthContext/AuthContext';

export const isProfileActivator = (control: Control) =>
  isActivator(control) && control?.functionality.id === 'profile';

const ProfileItem = ({ title }: { title?: string }) => {
  const { user } = useAuthContext();

  const { profiles } = useProfilesLocalStorage();
  const profile = profiles.find(p => p.userId === user?.userId);
  const initials = user?.initials;
  const { languageCode } = useAppFeaturesContext();
  const changeProfileText = getTranslation(
    'sidebar_changeProfile',
    languageCode
  );

  const iconClasses = cls({
    [styles.sidebarProfileIcon]: true,
    [styles.sideBarProfileIconSubAccounts]: true,
  });

  return (
    <>
      <span className={iconClasses} data-testid="sidebar-profile-icon">
        {profile && (
          <ProfileIconButton
            icon={profile.icon}
            imageAlt={getTranslation('sidebar_profileAvatar', languageCode)}
            text={initials}
            textSize="s"
          />
        )}
      </span>
      <span className={styles.sidebarItemTitle}>
        {profiles.length > 1 ? changeProfileText : title}
      </span>
    </>
  );
};

type ItemProps = {
  active: boolean;
  control: Control;
};

const Item = ({ active, control }: ItemProps) => {
  const LocalIcon = getAssetIconWithFallback(control, active);

  const itemIconClasses = cls({
    [styles.sidebarItemIcon]: true,
    [styles.sidebarItemIconActive]: active,
  });

  return (
    <>
      {LocalIcon && (
        <span className={itemIconClasses}>
          <LocalIcon />
        </span>
      )}
      <span className={styles.sidebarItemTitle}>{control.title}</span>
    </>
  );
};

type Props = {
  control: Control;
  id: string;
};

const SidebarItem = ({ control, id }: Props) => {
  const isProfileItem = isProfileActivator(control);

  const getItemElement = (control: Control, active: boolean) =>
    isProfileItem ? (
      <ProfileItem title={control.title} />
    ) : (
      <Item active={active} control={control} />
    );

  return (
    <ReactRouterLink
      pointer={control.destination}
      functionality={control.functionality}
      className={styles.sidebarMenuItem}
      id={id}
      dataTestId={`menu-item-${control.icon?.id || control.title}`}
    >
      {({ isActive }) => getItemElement(control, isActive)}
    </ReactRouterLink>
  );
};

export default SidebarItem;
